import { ListedInstrument } from '@eih-netbroker/common';
import { Box, LinearProgress, Popper, PopperProps } from '@mui/material';
import { AutocompleteInputChangeReason, AutocompleteRenderOptionState } from '@mui/material/Autocomplete/Autocomplete';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-line.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icons/information-fill.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search-line.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useInstrumentSearch } from '../../services/useInstrumentSearch';
import { cssWhen } from '../../utils/themeUtils';
import { useDebounce } from '../../utils/useDebounce';
import { NbAutocomplete, NbAutocompleteProps } from '../autocomplete/Autocomplete';
import { InstrumentResultRow } from '../instrument-result-row/InstrumentResultRow';
import classes from './InstrumentSearchAutocomplete.module.scss';

export type InstrumentSearchAutocompleteProps = Omit<NbAutocompleteProps<ListedInstrument>, 'options'> & {
  selection: ListedInstrument | null;
  onSelectInstrument?: (value: ListedInstrument | null) => void;
  isPopperWide?: boolean;
  clearSelectionAfterSelect?: boolean;
  isWithClearIcon?: boolean;
  placeholder?: string;
  transactionId?: string;
  wrapperClassName?: string;
};

export const InstrumentSearchAutocomplete = ({
  onSelectInstrument,
  isPopperWide,
  selection,
  clearSelectionAfterSelect,
  isWithClearIcon,
  placeholder,
  transactionId,
  wrapperClassName,
  ...props
}: InstrumentSearchAutocompleteProps) => {
  const [options, setOptions] = useState<Array<ListedInstrument>>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState<ListedInstrument | null>(selection || null);
  const firstPopupOpen = useRef(true);
  const previousSearchStr = useRef('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popperOverrides = useMemo(
    () =>
      isPopperWide
        ? {
            PopperComponent: (props: PopperProps) => (
              <Popper
                {...props}
                style={{ width: 350 }}
                placement="bottom-start"
              />
            ),
          }
        : {},
    [isPopperWide],
  );

  const { translation } = useLocale();
  const { searchInstrument } = useInstrumentSearch();
  const { debounce, isLoading, cancelDebounce } = useDebounce(setOptions);

  const openOptionsPopup = () => {
    if (inputValue.length > 0 && options.length > 0) {
      openPopup();
    }
  };

  const changeSelection = (e: SyntheticEvent<Element, Event> | null, value: ListedInstrument | null) => {
    if (!clearSelectionAfterSelect) {
      setSelectedInstrument(value);
    } else {
      setInputValue('');
      setSelectedInstrument(null);
      setOptions([]);
    }

    if (onSelectInstrument) {
      onSelectInstrument(value);
    }
    closePopup();
  };

  const clearSearchInput = () => {
    changeSelection(null, null);
  };

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    firstPopupOpen.current = true;
    setIsPopupOpen(false);
  };

  const onInputChange = useCallback(
    (_e: SyntheticEvent<Element, Event>, newInputValue: string, reason: AutocompleteInputChangeReason) => {
      setInputValue(newInputValue);
      props.onInputChange?.(_e, newInputValue, reason);
    },
    [setInputValue, props],
  );

  useEffect(() => {
    if (!inputValue.trim()) {
      cancelDebounce();
      setOptions([]);
      closePopup();
    } else if (selectedInstrument?.ticker !== inputValue && selectedInstrument?.ticker !== inputValue) {
      debounce(async () => {
        if (previousSearchStr.current === inputValue.trim()) {
          return [];
        }

        const result = await searchInstrument(inputValue.trim());
        if (firstPopupOpen.current) {
          firstPopupOpen.current = false;
          openPopup();
        }

        previousSearchStr.current = inputValue.trim();
        return result;
      });
    }
  }, [inputValue, debounce, searchInstrument, selectedInstrument?.ticker, transactionId, cancelDebounce]);

  useEffect(() => {
    if (!inputValue.trim() || selectedInstrument?.ticker === inputValue) {
      closePopup();
    } else {
      if (!isLoading && options.length > 0 && inputValue !== selectedInstrument?.ticker) {
        openPopup();
      }
    }
  }, [options, inputValue, isLoading, selectedInstrument?.ticker]);

  useEffect(() => {
    setInputValue(selection?.ticker ?? '');
    setSelectedInstrument(selection);
    setOptions([]);
    closePopup();
  }, [selection]);

  return (
    <div className={cssWhen(classes.autocompleteWrapper, wrapperClassName)}>
      <NbAutocomplete
        {...props}
        open={isPopupOpen}
        onOpen={openOptionsPopup}
        onClose={() => setIsPopupOpen(false)}
        size={'small'}
        getOptionLabel={option => (typeof option === 'string' ? option : option.ticker || option.ric)}
        isOptionEqualToValue={(option, value) => option.ric === value?.ric}
        filterOptions={options => options}
        options={options}
        inputValue={inputValue}
        onInputChange={onInputChange}
        value={selectedInstrument}
        onChange={changeSelection}
        placeholder={placeholder}
        renderOption={(renderOptionProps, option: ListedInstrument, state: AutocompleteRenderOptionState) => (
          <Box
            component="li"
            sx={{ padding: '0', margin: '0' }}
            {...renderOptionProps}
            key={option.ric}
            onMouseDown={e => changeSelection(e, option)}
          >
            <InstrumentResultRow
              isWithActions={false}
              instrument={option}
              key={option.ric}
              isSelected={state.selected}
            />
          </Box>
        )}
        {...popperOverrides}
        classes={{ paper: classes.searchResultPaper }}
        inputProps={{
          InputProps: {
            startAdornment: <SearchIcon />,
            className: classes.searchInput,
            endAdornment: inputValue.length > 0 && isWithClearIcon && (
              <CloseIcon
                onClick={clearSearchInput}
                className={classes.closeIcon}
              />
            ),
          },
          ...props.inputProps,
        }}
        noOptionsText={
          inputValue.length > 0 ? (
            <div className={classes.noOption}>
              <InfoIcon /> {translation.instrument.search.noResult}
            </div>
          ) : (
            <span></span>
          )
        }
      />
      {isLoading && <LinearProgress className={classes.progressIndicator} />}
    </div>
  );
};
